<template>
  <div class="space-y-1">
    <label
      v-if="label"
      :for="inputId"
      class="block text-sm font-medium text-gray-700"
    >
      {{ label }}
      <span v-if="required" class="text-danger-500">*</span>
    </label>

    <div class="relative">
      <div
        v-if="prefixIcon"
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
      >
        <i :class="prefixIcon" class="text-gray-400"></i>
      </div>

      <input
        :id="inputId"
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :class="inputClasses"
        v-bind="$attrs"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
      />

      <div
        v-if="suffixIcon || clearable"
        class="absolute inset-y-0 right-0 pr-3 flex items-center"
      >
        <button
          v-if="clearable && modelValue"
          @click="clear"
          type="button"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <i class="fas fa-times"></i>
        </button>
        <i v-else-if="suffixIcon" :class="suffixIcon" class="text-gray-400"></i>
      </div>
    </div>

    <p v-if="error" class="text-sm text-danger-600">{{ error }}</p>
    <p v-else-if="helper" class="text-sm text-gray-500">{{ helper }}</p>
  </div>
</template>

<script>
import { computed, ref } from 'vue';

export default {
  name: 'BaseInput',
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    helper: {
      type: String,
      default: '',
    },
    prefixIcon: {
      type: String,
      default: '',
    },
    suffixIcon: {
      type: String,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'blur', 'focus'],
  setup(props, { emit }) {
    const inputId = ref(`input-${Math.random().toString(36).substr(2, 9)}`);

    const inputClasses = computed(() => {
      const baseClass = 'input-base';
      const paddingClass = props.prefixIcon
        ? 'pl-10'
        : props.suffixIcon || props.clearable
          ? 'pr-10'
          : '';
      const errorClass = props.error
        ? 'border-danger-500 focus:ring-danger-500'
        : '';

      return [baseClass, paddingClass, errorClass].filter(Boolean).join(' ');
    });

    const handleInput = event => {
      const value = event.target.value;
      emit(
        'update:modelValue',
        props.type === 'number' ? Number(value) : value
      );
    };

    const handleBlur = event => {
      emit('blur', event);
    };

    const handleFocus = event => {
      emit('focus', event);
    };

    const clear = () => {
      emit('update:modelValue', '');
    };

    return {
      inputId,
      inputClasses,
      handleInput,
      handleBlur,
      handleFocus,
      clear,
    };
  },
};
</script>
